// DeviceSearchAutocomplete
// ------------------------
// Shared MUI Autocomplete that searches the global /api/devices endpoint as
// the user types. Used by AssignDeviceDialog and SwapImeiDialog to let
// admins find a device by partial IMEI, name, or model — or by regex via
// the `/pattern/` syntax that the backend list endpoint already supports.
//
// The query is debounced (300ms). Results are server-filtered, so we tell
// Autocomplete not to re-filter client-side (`filterOptions={(x) => x}`).
//
// Props:
//   value         — the currently selected device (or null)
//   onChange      — (device | null) => void
//   excludeIds    — Device _ids to hide from the dropdown (e.g. don't list
//                    the source device in a swap picker)
//   label, placeholder, helperText, autoFocus, disabled — passthrough
//   onMissNotice  — optional render-prop for the "no matches" state, lets
//                    callers offer a context-specific CTA (e.g. "create it
//                    on /devices first"). Receives the current input.

import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  InputAdornment,
  Chip,
} from "@mui/material";
import FingerprintIcon from "@mui/icons-material/FingerprintOutlined";
import api from "../../services/api";

const TYPE_LABEL = { 1: "Motor", 2: "Tank", 3: "Valve", 4: "Flow" };

export default function DeviceSearchAutocomplete({
  value,
  onChange,
  excludeIds = [],
  label = "Search IMEI",
  placeholder = "Type IMEI / name / model — wrap in /…/ for regex",
  helperText,
  autoFocus = false,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState(value?.IMEI || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Search on input change, debounced. Race guard via reqIdRef so a slow
  // earlier response doesn't overwrite the freshest one.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (inputValue || "").trim();
    if (q.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/devices", {
          params: { search: q, limit: 10 },
        });
        if (myReq !== reqIdRef.current) return; // stale response
        const items = (data?.data?.items || []).filter(
          (d) => !excludeIds.includes(d._id)
        );
        setOptions(items);
      } catch (_) {
        if (myReq === reqIdRef.current) setOptions([]);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // We deliberately don't depend on excludeIds — it's a stable prop in
    // practice, and re-running on every render of the parent would thrash
    // the network. If excludeIds genuinely changes, the next keystroke
    // picks it up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  // Detect when the input is being interpreted as regex on the backend.
  const isRegex = /^\/.+\/$/.test((inputValue || "").trim());

  return (
    <Autocomplete
      value={value}
      onChange={(_e, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_e, v) => setInputValue(v)}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x} // server does the filtering
      getOptionLabel={(o) => o?.IMEI || ""}
      isOptionEqualToValue={(o, v) => o?._id === v?._id}
      noOptionsText={
        (inputValue || "").trim().length < 2
          ? "Type at least 2 characters"
          : "No matches"
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: "block !important",
            py: 0.75,
          }}
        >
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8125rem",
              fontWeight: 600,
            }}
          >
            {option.IMEI}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", fontSize: "0.6875rem" }}
          >
            {option.device_name || "(no name)"} ·{" "}
            {TYPE_LABEL[option.device_type] || "type?"} ·{" "}
            {option.company?.name || "— unassigned —"}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          autoFocus={autoFocus}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <FingerprintIcon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isRegex && (
                  <Chip
                    size="small"
                    label="regex"
                    sx={{
                      height: 18,
                      fontSize: "0.625rem",
                      fontFamily: "monospace",
                      mr: 0.5,
                    }}
                  />
                )}
                {loading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontFamily: "monospace",
              fontSize: "0.875rem",
            },
          }}
        />
      )}
    />
  );
}
